import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MessageCircle, Phone, Mail } from "lucide-react";
import { Eyebrow, FadeUp, MaskLines } from "./Reveal";
import { CONTACT, useLang, waLink } from "@/lib/i18n";

const EMPTY = { name: "", phone: "", route_from: "", route_to: "", message: "" };
const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ as = "input", testid, ...props }) => {
  const C = as;
  return (
    <C
      data-testid={testid}
      {...props}
      className="w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-4 text-lg text-white placeholder:text-zinc-400 transition-colors focus:border-white focus:outline-none"
    />
  );
};

export default function Quote() {
  const { t, lang } = useLang();
  const q = t.quote;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.phone.trim().length >= 6 && form.message.trim().length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return toast.error(q.invalid);
    setBusy(true);
    try {
      await axios.post(`${API}/api/quotes`, { ...form, lang });
      toast.success(q.ok);
      setForm(EMPTY);
    } catch {
      toast.error(q.err);
    } finally {
      setBusy(false);
    }
  };

  const waText = [q.waIntro, form.route_from && `${q.from}: ${form.route_from}`, form.route_to && `${q.to}: ${form.route_to}`, form.message, form.name && `— ${form.name}`, form.phone && `Tel: ${form.phone}`].filter(Boolean).join("\n");

  return (
    <section id="quote" data-testid="quote-section" className="grain relative overflow-hidden border-t border-white/10 bg-[#0F1115]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-white/5 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-16 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <Eyebrow>{q.eyebrow}</Eyebrow>
          <h2 className="font-display text-6xl font-black uppercase italic leading-[0.88] sm:text-8xl">
            <MaskLines key={lang} lines={q.title} onView />
          </h2>
          <FadeUp><p className="mt-8 max-w-md text-base leading-relaxed text-zinc-300 md:text-lg">{q.lead}</p></FadeUp>
          <FadeUp delay={0.1} className="mt-12 space-y-4">
            <a data-testid="quote-call-link" href={CONTACT.tel} className="group flex items-center gap-4 font-display text-3xl font-bold italic sm:text-4xl">
              <Phone className="h-6 w-6 text-white" />
              <span className="bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_2px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 group-hover:bg-[length:100%_2px]">{CONTACT.phone}</span>
            </a>
            <a data-testid="quote-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-lg text-zinc-300 transition-colors hover:text-white">
              <Mail className="h-5 w-5 text-white" /> {CONTACT.email}
            </a>
          </FadeUp>
        </div>
        <FadeUp>
          <form onSubmit={submit} data-testid="quote-form" className="rounded-[28px] border border-white/10 bg-[#14171E]/80 p-6 backdrop-blur-xl sm:p-10">
            <div className="grid gap-x-8 sm:grid-cols-2">
              <Field testid="quote-form-name-input" placeholder={q.name} value={form.name} onChange={set("name")} maxLength={120} />
              <Field testid="quote-form-phone-input" type="tel" placeholder={q.phone} value={form.phone} onChange={set("phone")} required maxLength={40} />
              <Field testid="quote-form-route-from-input" placeholder={q.from} value={form.route_from} onChange={set("route_from")} maxLength={120} />
              <Field testid="quote-form-route-to-input" placeholder={q.to} value={form.route_to} onChange={set("route_to")} maxLength={120} />
            </div>
            <Field as="textarea" testid="quote-form-message-input" rows={4} placeholder={q.msg} value={form.message} onChange={set("message")} required maxLength={3000} />
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <button data-testid="quote-form-submit-button" disabled={busy} className="group flex items-center justify-center gap-3 rounded-full bg-white px-8 py-4 font-semibold text-black transition-colors hover:bg-zinc-300 disabled:opacity-60">
                {busy ? q.sending : q.send}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a data-testid="quote-form-whatsapp-button" href={waLink(waText)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-sm text-zinc-300 transition-colors hover:text-white">
                <MessageCircle className="h-4 w-4" /> {q.viaWa}
              </a>
            </div>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
